import { without, flatMap, fromPairs, debounce } from 'lodash-es'

import Api from '@/api'
import { STATUS_5 } from '@/user'

const getResultsKey = (searchProfile, sort) => {
  return `${searchProfile.tagKeys.join('-')}.${searchProfile.customTags.join('-')}.${sort}`
}

const makeItSo = (configs) => {
  return store => {
    configs.forEach(({ updateAction, changeMutation, upToDateGetter }) => {
      store.subscribe((mutation, state) => {
        if (mutation.type === changeMutation) {
          store.dispatch(updateAction)
        }
      })
    })
  }
}

export default {
  plugins: [makeItSo([
    {
      changeMutation: 'resultsJobSelectedSet',
      upToDateGetter: 'jobUpToDate',
      updateAction: 'jobUpdate'
    }
  ])],

  state: {
    resultsCount: 0,
    resultsLoading: false,
    resultsLoadingKey: null,
    resultsKey: '',
    resultsPagination: null,
    resultsJobSelected: null,
    resultsSidebar: null,
    resultsMatchingProfile: null,
    resultsFenceUrl: null,
    categories: [],
    modalOpen: false,
    modalComponent: null,
    modalProps: null,
    isFilterOpen: false,
    isSearchOpen: false,
    searchProfile: null,
    sort: 'STANDARD',
    headline: '',
    results: [],
    bookmarks: [],

    jobLoadingId: null,
    job: null
  },
  mutations: {
    jobLoadingIdSet (state, id) {
      state.jobLoadingId = id
    },
    jobSet (state, job) {
      state.job = job
    },
    bookmarksSet (state, bookmarks) {
      state.bookmarks = bookmarks
    },
    resultsCountSet (state, count) {
      state.resultsCount = count
    },
    resultsLoadingSet (state, loading) {
      state.resultsLoading = loading
    },
    modalOpen (state, { component, props }) {
      state.modalComponent = component
      state.modalProps = props
      state.modalOpen = true
    },
    modalClose (state) {
      state.modalComponent = null
      state.modalProps = null
      state.modalOpen = false
    },
    searchToggle (state) {
      state.isSearchOpen = !state.isSearchOpen
    },
    searchProfileAddTag (state, tagKey) {
      EventBus.trigger('filter-settings:add', { type: 'tag', key: tagKey })
      state.searchProfile.tagKeys = [...(new Set(state.searchProfile.tagKeys)).add(tagKey)]
      state.resultsLoading = true
    },
    searchProfileAddTags (state, tagKeys) {
      const set = new Set(state.searchProfile.tagKeys)

      tagKeys.forEach(tagKey => {
        EventBus.trigger('filter-settings:add', { type: 'tag', key: tagKey })
        set.add(tagKey)
      })

      state.searchProfile.tagKeys = [...set]
      state.resultsLoading = true
    },
    searchProfileRemoveCustomTag (state, tagKey) {
      EventBus.trigger('filter-settings:remove', { type: 'search-term', key: tagKey })
      state.searchProfile.customTags = without(state.searchProfile.customTags, tagKey)
      state.resultsLoading = true
    },
    searchProfileRemoveTag (state, tagKey) {
      EventBus.trigger('filter-settings:remove', { type: 'tag', key: tagKey })
      state.searchProfile.tagKeys = without(state.searchProfile.tagKeys, tagKey)
      state.resultsLoading = true
    },
    searchProfileRemoveTags (state, tagKeys) {
      tagKeys.forEach(tagKey => {
        EventBus.trigger('filter-settings:remove', { type: 'tag', key: tagKey })
      })

      state.searchProfile.tagKeys = without(state.searchProfile.tagKeys, ...tagKeys)
      state.resultsLoading = true
    },
    searchProfileClear (state) {
      if (state.searchProfile.tagKeys.length === 0 && state.searchProfile.customTags.length === 0) {
        return
      }

      state.searchProfile.tagKeys.forEach(tagKey => {
        EventBus.trigger('filter-settings:remove', { type: 'tag', key: tagKey })
      })
      state.searchProfile.customTags.forEach(tagKey => {
        EventBus.trigger('filter-settings:remove', { type: 'search-term', key: tagKey })
      })

      state.resultsLoading = true
      state.searchProfile.tagKeys = []
      state.searchProfile.customTags = []
    },
    filterOpen (state) {
      state.isFilterOpen = true
    },
    filterClose (state) {
      state.isFilterOpen = false
    },
    filterToggle (state) {
      state.isFilterOpen = !state.isFilterOpen
    },
    sortSet (state, newSort) {
      EventBus.trigger('filter-settings:sort', newSort)
      state.sort = newSort
    },
    resultsSet (state, { results, resultsPagination, resultsCount, resultsKey, append, sidebar, matchingProfile, fenceUrl }) {
      if (append) {
        state.results = state.results.concat(results)
      } else {
        state.results = results
      }
      state.resultsCount = resultsCount
      state.resultsKey = resultsKey
      state.resultsPagination = resultsPagination
      state.resultsSidebar = sidebar
      state.resultsMatchingProfile = matchingProfile
      state.resultsFenceUrl = fenceUrl
    },
    resultsLoadingKeySet (state, key) {
      state.resultsLoadingKey = key
    },
    resultsJobSelectedSet (state, jobId) {
      state.resultsJobSelected = jobId
    }
  },
  actions: {
    bookmarksToggle ({ state, commit, dispatch }, jobId) {
      const id = Number.parseInt(jobId)
      const idx = state.bookmarks.indexOf(id)
      if (idx === -1) {
        return Api.bookmarksAdd(jobId).then((data) => {
          commit('bookmarksSet', [...state.bookmarks, id])
          EventBus.trigger('bookmarks:added', jobId)
          EventBus.trigger('snackbar:message', {
            body: data.snackbar,
            handlers: []
          })
        })
      } else {
        return Api.bookmarksRemove(jobId).then((data) => {
          const newBookmarks = [...state.bookmarks]
          newBookmarks.splice(idx, 1)
          commit('bookmarksSet', newBookmarks)
          EventBus.trigger('bookmarks:removed', jobId)
          EventBus.trigger('snackbar:message', {
            body: data.snackbar,
            handlers: [{
              selector: '.js-snackbar__button',
              eventName: 'click',
              handler: () => dispatch('bookmarksToggle', jobId)
            }]
          })
        })
      }
    },
    bookmarksGet ({ commit }) {
      Api.bookmarksAll().then((bookmarks) => {
        commit('bookmarksSet', bookmarks)
      })
    },
    setupLegacy ({ state, getters, commit }) {
      getters.checkedTags.forEach(tag => {
        EventBus.trigger('filter-settings:add', { type: 'tag', key: tag.key })
      })
      state.searchProfile.customTags.forEach(tag => {
        EventBus.trigger('filter-settings:add', { type: 'search-term', key: tag })
      })
      EventBus.trigger('filter-settings:setup-complete', null)
      EventBus.on('filter-settings:results-count', (_event, count) => {
        commit('resultsCountSet', count)
        commit('resultsLoadingSet', false)
      })
    },
    searchProfileSave ({ state, getters }, { apiUrl }) {
      if (getters.isUserStatus(STATUS_5)) {
        return Api.saveSearch(apiUrl, state.searchProfile).then(data => {
          EventBus.trigger('snackbar:message', {
            body: data,
            handlers: []
          })
        })
      } else {
        return Api.postSaveSearch(apiUrl, state.searchProfile)
      }
    },

    resultsUpdate ({ state, commit, getters, dispatch }) {
      const key = getResultsKey(state.searchProfile, state.sort)

      if (!getters.resultsUpToDate || state.resultsLoadingKey !== key) {
        commit('resultsLoadingKeySet', key)
        commit('resultsLoadingSet', true)
        commit('resultsJobSelectedSet', null)
        commit('jobSet', null)
        return Api.resultsLoad(state.searchProfile, state.sort).then(data => {
          dispatch('resultsAfterUpdate', { data, key })
        })
      }
    },

    resultsAfterUpdate ({ commit, getters }, { data, key, append }) {
      if (key === getters.desiredResultsKey) {
        commit('resultsSet', { results: data.results, resultsCount: data.count, resultsKey: key, resultsPagination: data.pagination, append, sidebar: data.sidebar, matchingProfile: data.matchingProfile, fenceUrl: data.fenceUrl })
        commit('resultsLoadingSet', false)
        // TODO: use Vue Router instead
        history.pushState({}, '', data.url)
      }
    },

    jobUpdate ({ state, commit, getters, dispatch }) {
      const jobId = state.resultsJobSelected
      if (jobId && (!getters.jobUpToDate && state.jobLoadingId !== jobId)) {
        commit('jobLoadingIdSet', jobId)
        commit('jobSet', null)
        dispatch('debouncedJobUpdate', jobId)
      }
    },

    debouncedJobUpdate: debounce(({ dispatch }, jobId) => {
      return Api.jobLoad(jobId).then(data => {
        dispatch('jobAfterUpdate', { data, jobId })
      })
    }, 300, { leading: true, trailing: true }),

    jobAfterUpdate ({ state, commit, getters }, { data, jobId }) {
      if (jobId === state.resultsJobSelected) {
        commit('jobLoadingIdSet', null)
        commit('jobSet', { ...data, id: jobId })
      }
    },

    resultsPaginationNext ({ state, commit, getters, dispatch }) {
      if (state.resultsPagination && !state.resultsPagination.isLast) {
        const key = getResultsKey(state.searchProfile, state.sort)

        return Api.resultsLoad(state.searchProfile, state.sort, state.resultsPagination.current + 1).then(data => {
          dispatch('resultsAfterUpdate', { data, key, append: true })
        })
      }
    },
    resultsSelectNext ({ state, getters, dispatch, commit }) {
      if (getters.isResultsNextAvailable) {
        commit('resultsJobSelectedSet', state.results[getters.resultsJobSelectedIndex + 1].id)
      }
    },
    resultsSelectPrev ({ state, getters, dispatch, commit }) {
      if (getters.isResultsPrevAvailable) {
        commit('resultsJobSelectedSet', state.results[getters.resultsJobSelectedIndex - 1].id)
      }
    }
  },
  getters: {
    isBookmarked: (_state, { bookmarksMap }) => (jobId) => {
      return bookmarksMap[jobId]
    },
    bookmarksMap: ({ bookmarks }) => {
      return bookmarks.reduce((acc, val) => {
        acc[val] = true
        return acc
      }, {})
    },
    isResultsNextAvailable ({ results }, { resultsJobSelectedIndex }) {
      return resultsJobSelectedIndex + 1 !== results.length
    },
    isResultsPrevAvailable ({ results }, { resultsJobSelectedIndex }) {
      return resultsJobSelectedIndex - 1 >= 0
    },
    isUserStatus: ({ user }) => (status) => {
      return user && user.status === status
    },
    resultsJobSelectedIndex ({ results, resultsJobSelected }) {
      return results.findIndex(job => job.id === resultsJobSelected)
    },
    flatTagMap ({ categories }) {
      return fromPairs(flatMap(categories, category => {
        return flatMap(category.grouped_category_tags, group => {
          return group.group_tags.map(tag => [tag.key, tag])
        })
      }))
    },
    checkedTagsMap (_state, { checkedTags }) {
      return (checkedTags && fromPairs(checkedTags.map(tag => [tag.key, tag]))) || {}
    },
    checkedTags ({ searchProfile }, { flatTagMap }) {
      return (searchProfile && searchProfile.tagKeys.map(tagKey => flatTagMap[tagKey])) || []
    },
    desiredResultsKey ({ searchProfile, sort }, { checked }) {
      return searchProfile && getResultsKey(searchProfile, sort)
    },
    resultsUpToDate: ({ resultsKey }, { desiredResultsKey }) => desiredResultsKey === resultsKey,
    jobUpToDate: ({ job, resultsJobSelected }) => !resultsJobSelected || (job && job.id === resultsJobSelected)
  }
}
